import {
  addCartGoods,
  getShopShoppingCart,
  removeCartGoods,
  updateCartGoods,
} from "api/user"
import useAreaCookie from "hooks/useAreaCookie"
import useLocalPayment from "hooks/useLocalPayment"
import { numberAdd, numberMutiply } from "libs/compute"
import { DEFAULT_COOKIE_AREASYSNO } from "libs/cons"
import {
  CartContext,
  LocContext,
  MediaQueryContext,
  UserContext,
} from "libs/contexts"
import { CartTypeEnum } from "libs/enums"
import { buildPeriodList, isDisabledShopSku } from "libs/utils"
import { useRouter } from "next/router"
import { clone } from "rambdax"
import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useMeta } from "reactutils"

type CartContextProviderProps = {
  children: React.ReactNode
}

function CartContextProvider({ children }: CartContextProviderProps) {
  const userInfo = useContext(UserContext)
  const { location } = useContext(LocContext)
  const { isMobile } = useContext(MediaQueryContext)
  const { updateLocalPayment, updateLocalTradeAgreement } = useLocalPayment()
  const { areaSysNo: areaCookieSysNo } = useAreaCookie()
  const [hasDisabled, setHasDisabled] = useState(true)
  const [cartList, setCartList] = useState<NewCartInfo[]>([])
  const [areaSysNo, setAreaSysNo] = useState("")
  const route = useRouter()

  useEffect(() => {
    if (isMobile) {
      // h5 用手机定位
      setAreaSysNo(location?.sysNo as string)
    } else {
      setAreaSysNo(areaCookieSysNo)
    }
  }, [areaCookieSysNo, isMobile, location.sysNo])

  async function fetchShoppingCart(extraCondition: Object = {}) {
    if (!areaSysNo || areaSysNo === DEFAULT_COOKIE_AREASYSNO || !userInfo)
      return
    const json = {
      ...extraCondition,
      type: CartTypeEnum.SHOP,
      areaSysNo: areaSysNo,
    }
    const res = await getShopShoppingCart(json)
    if (res?.code === 200 && res?.data?.categoryTypeList?.length) {
      const cartData = res?.data
      const cartList = [...res?.data?.categoryTypeList]
      cartList.forEach((item: NewCartInfo) => {
        item.supplierSysNo = item.shopDto.supplierSysNo
        item.shopSysNo = item.shopDto.sysNo
        item.periodGroupList.forEach((periodGroupItem) => {
          const tradeAgreementListViewForECDTOS =
            clone(periodGroupItem?.tradeAgreementListViewForECDTOS) || []
          // 购物车页面才写协议和交易方式的localStorage
          if (
            tradeAgreementListViewForECDTOS.length &&
            route.pathname === "/pages/cart"
          ) {
            const activeTradeAgreement = tradeAgreementListViewForECDTOS.find(
              (i) => i.sysNo === periodGroupItem.agreementSysNo,
            )
            if (activeTradeAgreement) {
              activeTradeAgreement.active = true
              updateLocalTradeAgreement(
                periodGroupItem.agreementSysNo,
                item.supplierSysNo,
              )
              if (!!activeTradeAgreement.billingPeriodOtherDays) {
                periodGroupItem.billingPeriodOtherDays =
                  activeTradeAgreement.billingPeriodOtherDays
              }
              periodGroupItem.discountByMethodList = buildPeriodList(
                activeTradeAgreement.discountByMethodList || [],
                item.supplierSysNo,
                activeTradeAgreement.billingPeriodOtherDays,
              )
              if (periodGroupItem.discountByMethodList.length) {
                var activeDiscount = periodGroupItem.discountByMethodList.find(
                  (i) =>
                    i.accountPeriod === periodGroupItem.accountPeriod &&
                    i.billingPeriod === periodGroupItem.billingPeriod,
                )
                if (activeDiscount) {
                  activeDiscount.active = true
                  updateLocalPayment(activeDiscount.sysNo, item.supplierSysNo)
                  periodGroupItem.customPeriodSysNo = activeDiscount.sysNo
                  periodGroupItem.paymentType = activeDiscount.accountPeriod
                  periodGroupItem.billingPeriod = activeDiscount.billingPeriod
                  periodGroupItem.priceRate = activeDiscount.discount / 100
                }
              }
            }
          }
          periodGroupItem.tradeAgreementListViewForECDTOS = tradeAgreementListViewForECDTOS
          periodGroupItem.skuList.forEach((sku: SkuInfo) => {
            sku.noAgreementCanSale = periodGroupItem.noAgreementCanSale
            if (
              tradeAgreementListViewForECDTOS?.length &&
              periodGroupItem.agreementSysNo
            ) {
              sku.tradeAgreementSysNo = periodGroupItem.agreementSysNo
              const tradeAgreement = tradeAgreementListViewForECDTOS.find(
                (i) => i.active,
              )
              if (
                tradeAgreement &&
                tradeAgreement?.skuForPriceCalculate?.length
              ) {
                const skuForPriceCalculate = tradeAgreement.skuForPriceCalculate.find(
                  (calc) => calc.sysNo === sku.mallSku.sysNo,
                )
                sku.skuForPriceCalculate = skuForPriceCalculate
              }
            }

            if (hasDisabled) {
              sku.disabled = isDisabledShopSku(
                sku,
                tradeAgreementListViewForECDTOS,
              )
              sku.selected = !sku.disabled
              if (
                (sku.agreementCanSale &&
                  sku.mallSku.sourceType === 3 &&
                  !sku.mallSku.salesPrice) ||
                sku.quantity <= 0 ||
                (!periodGroupItem.noAgreementCanSale &&
                  !tradeAgreementListViewForECDTOS?.length)
              ) {
                sku.selected = false
              }
            } else {
              sku.disabled = false
              sku.selected = false
            }
          })
        })

        item.disabled = item.periodGroupList.every((periodGroupItem) => {
          return periodGroupItem.skuList.every((i) => i.disabled)
        })
        if (item.disabled) {
          item.selected = false
        } else {
          let selected = true
          for (let periodGroupItem of item.periodGroupList) {
            if (!selected) break
            for (let skuItem of periodGroupItem.skuList) {
              if (!skuItem.selected && !skuItem.disabled) {
                selected = false
                break
              }
            }
          }
          item.selected = selected
        }
        item.periodGroupList.sort(
          (a, b) => +a.categoryGoodsTypeSysNo - +b.categoryGoodsTypeSysNo,
        )
      })
      setCartList([...cartList])

      return { cartData }
    }

    setCartList([])
    return null
  }

  const { data, refetch, loading } = useMeta(fetchShoppingCart, [
    areaSysNo,
    userInfo,
    hasDisabled,
  ])

  const resetCartList = useCallback((data: NewCartInfo[]) => {
    setCartList(data)
  }, [])

  function refreshSelected(list) {
    list.forEach((shop: NewCartInfo) => {
      shop.periodGroupList.forEach((periodGroupItem) => {
        periodGroupItem.skuList.forEach((item) => {
          item.disabled = isDisabledShopSku(
            item,
            periodGroupItem.tradeAgreementListViewForECDTOS,
          )
          item.selected = !item.disabled
          if (
            (item.agreementCanSale &&
              item.mallSku.sourceType === 3 &&
              !item.mallSku.salesPrice) ||
            item.quantity <= 0
          ) {
            item.selected = false
          }
        })
        shop.disabled = periodGroupItem.skuList.every((i) => i.disabled)
        if (shop.disabled) {
          shop.selected = false
        } else {
          shop.selected = periodGroupItem.skuList
            .filter((i) => !i.disabled)
            .every((i) => i.selected)
        }
      })
    })
    return list
  }

  const addCart = useCallback(
    async (
      params: AddCartGoodsProps,
      doneFn?: Function,
      catchFn?: Function,
    ) => {
      const res = await addCartGoods(params)
      if (res?.code === 200 && res.data) {
        setHasDisabled(true)
        doneFn?.()
      } else {
        catchFn?.(res?.message || "服务器系统内部错误")
      }
    },
    [],
  )

  // 更新商品数量
  const updateCart = useCallback(
    async (sysNo: string, quantity: number) => {
      const dataList = [...cartList]
      const res = await updateCartGoods(sysNo, quantity)
      if (res?.code === 200 && res.data) {
        dataList?.forEach((item) => {
          item.periodGroupList.forEach((periodGroupItem) => {
            periodGroupItem.skuList.forEach((sku) => {
              if (sku.sysNo === sysNo) {
                sku.quantity = quantity
              }
            })
          })
        })
        setCartList([...cartList])
      } else {
        setCartList([...cartList])
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cartList],
  )

  // 删除商品数量
  const removeCart = useCallback(
    async (sysNoList: string[], doneFn?: Function, catchFn?: Function) => {
      const disabled = hasDisabled
      const res = await removeCartGoods(sysNoList)
      if (res?.code === 200 && res.data) {
        if (disabled) {
          setHasDisabled(false)
        } else {
          fetchShoppingCart()
        }
        doneFn?.()
      } else {
        catchFn?.(res?.message || "服务器系统内部错误")
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasDisabled],
  )

  function buildCartReturnData(): {
    skuList: Array<SkuInfo>
    quantity: number
    totalAmount: number
  } {
    const skuList = []
    let quantity = 0
    let totalAmount = 0
    cartList.forEach((shop) => {
      shop.periodGroupList?.forEach((periodGroupItem) => {
        periodGroupItem.skuList.forEach((skuItem) => {
          skuList.push(skuItem)
          quantity = +numberAdd(quantity, skuItem.quantity)
          const amount = +numberMutiply(
            skuItem.quantity,
            skuItem.mallSku.salesPrice,
          )
          totalAmount = +numberAdd(totalAmount, amount)
        })
      })
    })
    return {
      skuList,
      quantity,
      totalAmount,
    }
  }

  const cartContextValue = useMemo(
    () => ({
      cartData: data?.cartData,
      cartList,
      skuList: buildCartReturnData().skuList,
      quantity: buildCartReturnData().quantity, // 统计数量
      totalAmount: buildCartReturnData().totalAmount, // 统计总金额
      refetchCart: refetch,
      resetCartList,
      addCart,
      updateCart,
      removeCart,
      loading,
      refreshData: fetchShoppingCart,
      refreshSelected,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.cartData, cartList, loading],
  )

  return (
    <CartContext.Provider value={cartContextValue}>
      {children}
    </CartContext.Provider>
  )
}

export default CartContextProvider
